import { v4 as uuid } from 'uuid';
import { Logger } from '@quickplay/foundation';
import { BrightcoveDataCollectionConfig } from '../model/BrightcoveDataCollectionConfig';
/**
 * Tracker for Brightcove Data Collection API v2 events.
 *
 * Handles the construction and transmission of analytics events to Brightcove's
 * metrics tracker endpoint. All events are sent via image beacons to avoid CORS issues.
 *
 * @see https://apis.support.brightcove.com/data-collection/references/reference.html
 */
export class BrightcoveDataCollectionTracker {
  private readonly TRACKER_URL = 'https://metrics.brightcove.com/v2/tracker';
  private readonly sessionId: string;
  private readonly config: BrightcoveDataCollectionConfig;
  private readonly logger: Logger;
  private readonly debugLogging: boolean;
  private readonly beaconImages: Set<HTMLImageElement> = new Set();

  constructor(config: BrightcoveDataCollectionConfig, logger: Logger) {
    this.config = config;
    this.logger = logger;
    this.debugLogging = config.debugLogging ?? false;
    this.sessionId = config.sessionId || uuid();

    if (this.debugLogging) {
      this.logger.log(`[DATA_COLLECTION] Session initialized: ${this.sessionId}`);
    }
  }

  /**
   * Clean up resources when tracker is destroyed.
   * Removes all beacon images from DOM.
   */
  public destroy(): void {
    for (const img of this.beaconImages) {
      if (img.parentNode) {
        img.remove();
      }
    }
    this.beaconImages.clear();
  }

  /**
   * Gets the current session ID.
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Tracks a player_load event.
   * This event marks the beginning of a tracking session and should be sent first.
   */
  trackPlayerLoad(): void {
    const params = this.buildCommonParams();
    params.set('event', 'player_load');

    this.sendBeacon('player_load', params);
  }

  /**
   * Tracks a video_impression event.
   * Fired when video metadata is loaded and player is ready for view.
   */
  trackVideoImpression(): void {
    const params = this.buildCommonParams();
    params.set('event', 'video_impression');
    params.set('video', this.config.videoId);
    if (this.config.videoName) {
      params.set('video_name', this.config.videoName);
    }

    this.sendBeacon('video_impression', params);
  }

  /**
   * Tracks a play_request event.
   * Fired when playback is requested (manual or automatic via autoplay).
   */
  trackPlayRequest(): void {
    const params = this.buildCommonParams();
    params.set('event', 'play_request');
    params.set('video', this.config.videoId);
    if (this.config.videoName) {
      params.set('video_name', this.config.videoName);
    }

    this.sendBeacon('play_request', params);
  }

  /**
   * Tracks a video_view event.
   * Fired when actual video playback starts. Only one per viewing session.
   *
   * @param startTimeMs - Time in milliseconds between playback initiation and first frame render (optional)
   */
  trackVideoView(startTimeMs?: number): void {
    const params = this.buildCommonParams();
    params.set('event', 'video_view');
    params.set('video', this.config.videoId);
    if (this.config.videoName) {
      params.set('video_name', this.config.videoName);
    }
    // start_time_ms: should be a number, not string, and should exclude ad time (handled by caller)
    if (typeof startTimeMs === 'number' && startTimeMs > 0) {
      params.set('start_time_ms', Math.floor(startTimeMs).toString());
    }

    this.sendBeacon('video_view', params);
  }

  /**
   * Tracks a video_engagement event.
   * Sent periodically during playback to report QoE metrics.
   */
  // eslint-disable-next-line complexity
  trackVideoEngagement(
    currentTime: number,
    duration: number | undefined,
    rangeStart: number,
    metrics?: {
      renditionUrl?: string;
      renditionBitrate?: number;
      renditionMimeType?: string;
      renditionHeight?: number;
      renditionWidth?: number;
      rebufferingSeconds?: number;
      rebufferingCount?: number;
      forwardBufferSeconds?: number;
      measuredBps?: number;
      mediaBytesTransferred?: number;
      mediaSecondsLoaded?: number;
      playerWidth?: number;
      playerHeight?: number;
      droppedFrames?: number;
    },
  ): void {
    const params = this.buildCommonParams();
    params.set('event', 'video_engagement');
    params.set('video', this.config.videoId);
    if (this.config.videoName) {
      params.set('video_name', this.config.videoName);
    }

    // Handle engagement range/viewing data based on stream type
    this.addEngagementRangeParams(params, currentTime, duration, rangeStart);

    // Add QoE metrics if provided
    if (metrics) {
      this.addQoEMetrics(params, metrics);
    }

    this.sendBeacon('video_engagement', params);
  }

  /**
   * Adds engagement range or video_seconds_viewed parameter based on stream type.
   */
  private addEngagementRangeParams(
    params: URLSearchParams,
    currentTime: number,
    duration: number | undefined,
    rangeStart: number,
  ): void {
    if (typeof duration === 'number' && duration > 0) {
      params.set('video_duration', Math.floor(duration).toString()); // integer <int32>
    }
    // Range format: startSecond..endSecond
    const rangeStartSeconds = Math.floor(rangeStart);
    const rangeEndSeconds = Math.floor(currentTime);
    params.set('range', `${rangeStartSeconds}..${rangeEndSeconds}`);
  }

  /**
   * Adds QoE metrics to the engagement event parameters.
   */
  private addQoEMetrics(
    params: URLSearchParams,
    metrics: {
      renditionUrl?: string;
      renditionBitrate?: number;
      renditionMimeType?: string;
      renditionHeight?: number;
      renditionWidth?: number;
      rebufferingSeconds?: number;
      rebufferingCount?: number;
      forwardBufferSeconds?: number;
      measuredBps?: number;
      mediaBytesTransferred?: number;
      mediaSecondsLoaded?: number;
      playerWidth?: number;
      playerHeight?: number;
      droppedFrames?: number;
    },
  ): void {
    if (metrics.renditionUrl) params.set('rendition_url', metrics.renditionUrl);
    if (typeof metrics.renditionBitrate === 'number')
      params.set('rendition_indicated_bps', Math.floor(metrics.renditionBitrate).toString());
    if (metrics.renditionMimeType) params.set('rendition_mime_type', metrics.renditionMimeType);
    if (typeof metrics.renditionHeight === 'number')
      params.set('rendition_height', Math.floor(metrics.renditionHeight).toString());
    if (typeof metrics.renditionWidth === 'number')
      params.set('rendition_width', Math.floor(metrics.renditionWidth).toString());
    if (typeof metrics.rebufferingSeconds === 'number') {
      params.set('rebuffering_seconds', metrics.rebufferingSeconds.toString()); // number
    }
    if (typeof metrics.rebufferingCount === 'number') {
      params.set('rebuffering_count', Math.floor(metrics.rebufferingCount).toString()); // integer
    }
    if (typeof metrics.forwardBufferSeconds === 'number') {
      params.set('forward_buffer_seconds', metrics.forwardBufferSeconds.toString()); // number
    }
    if (typeof metrics.measuredBps === 'number') params.set('measured_bps', metrics.measuredBps.toString());
    if (typeof metrics.mediaBytesTransferred === 'number')
      params.set('media_bytes_transferred', Math.floor(metrics.mediaBytesTransferred).toString());
    if (typeof metrics.mediaSecondsLoaded === 'number')
      params.set('media_seconds_loaded', Math.floor(metrics.mediaSecondsLoaded).toString());
    if (typeof metrics.playerWidth === 'number') params.set('player_width', Math.floor(metrics.playerWidth).toString());
    if (typeof metrics.playerHeight === 'number')
      params.set('player_height', Math.floor(metrics.playerHeight).toString());
    if (typeof metrics.droppedFrames === 'number')
      params.set('dropped_frames', Math.floor(metrics.droppedFrames).toString());
  }

  /**
   * Tracks ad_mode_begin event.
   * Fired when control is handed to advertising agent (start of ad break).
   */
  trackAdModeBegin(): void {
    const params = this.buildCommonParams();
    params.set('event', 'ad_mode_begin');

    this.sendBeacon('ad_mode_begin', params);
  }

  /**
   * Tracks ad_mode_complete event.
   * Fired when control is returned from advertising agent (end of ad break).
   */
  trackAdModeComplete(): void {
    const params = this.buildCommonParams();
    params.set('event', 'ad_mode_complete');

    this.sendBeacon('ad_mode_complete', params);
  }

  /**
   * Tracks an error event.
   * Fired when playback errors occur.
   *
   * @param errorCode - Platform-specific error code or error message
   */
  trackError(errorCode: string | number): void {
    const params = this.buildCommonParams();
    params.set('event', 'error');
    params.set('error_code', errorCode.toString());

    this.sendBeacon('error', params);
  }

  /**
   * Builds the set of common parameters required for all events.
   */
  // eslint-disable-next-line complexity
  private buildCommonParams(): URLSearchParams {
    const params = new URLSearchParams();

    // Mandatory parameters
    params.set('domain', 'videocloud');
    params.set('account', this.config.accountId.toString());
    params.set('session', this.sessionId);

    // Timestamp (epoch milliseconds)
    params.set('time', Date.now().toString());

    // Optional: destination and source (page context)
    const destination = this.config.destination || this.getDocumentLocation();
    if (destination) {
      params.set('destination', destination);
    }

    const source = this.config.source || this.getDocumentReferrer();
    if (source) {
      params.set('source', source);
    }

    // Add optional user and device information
    this.addUserDeviceParams(params);

    // Add optional geographic information
    this.addGeographicParams(params);

    // Add optional player context
    this.addPlayerContextParams(params);

    return params;
  }

  /**
   * Adds optional user and device parameters.
   */
  private addUserDeviceParams(params: URLSearchParams): void {
    if (this.config.userId) params.set('user', this.config.userId);
    if (this.config.deviceType) params.set('device_type', this.config.deviceType);
    if (this.config.deviceOs) params.set('device_os', this.config.deviceOs);
    if (this.config.deviceOsVersion) params.set('device_os_version', this.config.deviceOsVersion);
    if (this.config.deviceManufacturer) params.set('device_manufacturer', this.config.deviceManufacturer);
    if (this.config.browserType) params.set('browser_type', this.config.browserType);
  }

  /**
   * Adds optional geographic parameters.
   */
  private addGeographicParams(params: URLSearchParams): void {
    if (this.config.country) params.set('country', this.config.country);
    if (this.config.countryName) params.set('country_name', this.config.countryName);
    if (this.config.region) params.set('region', this.config.region);
    if (this.config.regionName) params.set('region_name', this.config.regionName);
    if (this.config.city) params.set('city', this.config.city);
    if (this.config.dma) params.set('dma', this.config.dma);
  }

  /**
   * Adds optional player context parameters.
   */
  private addPlayerContextParams(params: URLSearchParams): void {
    if (this.config.playerId) params.set('player', this.config.playerId);
    if (this.config.playerName) params.set('player_name', this.config.playerName);
    if (this.config.application) params.set('application', this.config.application);
  }

  /**
   * Sends a beacon request to the tracker endpoint.
   * Creates a new image element for each beacon to prevent request cancellation.
   *
   * @param eventType - Type of event for logging purposes
   * @param params - URLSearchParams containing event data
   */
  private sendBeacon(eventType: string, params: URLSearchParams): void {
    try {
      if (typeof document === 'undefined') {
        if (this.debugLogging) {
          this.logger.log(`[DATA_COLLECTION] Document not available, skipping beacon for: ${eventType}`);
        }
        return;
      }

      const url = `${this.TRACKER_URL}?${params.toString()}`;
      const beaconImage = document.createElement('img');
      beaconImage.style.display = 'none';

      // Clean up image from DOM and Set after request completes
      const cleanup = () => {
        if (beaconImage.parentNode) {
          beaconImage.remove();
        }
        this.beaconImages.delete(beaconImage);
      };

      beaconImage.addEventListener('load', cleanup);
      beaconImage.addEventListener('error', cleanup);

      // Add to tracking set and DOM before setting src
      this.beaconImages.add(beaconImage);
      document.body.appendChild(beaconImage);

      // Set src to trigger the request
      beaconImage.src = url;

      if (this.debugLogging) {
        this.logger.log(`[DATA_COLLECTION] Event sent: ${eventType}`);
      }
    } catch (error) {
      if (this.debugLogging) {
        this.logger.log(`[DATA_COLLECTION] Error sending beacon for ${eventType}:`, error);
      }
    }
  }

  /**
   * Gets the current document location (destination URL).
   */
  private getDocumentLocation(): string | undefined {
    try {
      return globalThis.window?.location?.href;
    } catch {
      return undefined;
    }
  }

  /**
   * Gets the current document referrer (source URL).
   */
  private getDocumentReferrer(): string | undefined {
    try {
      return document?.referrer;
    } catch {
      return undefined;
    }
  }
}
